import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from '@/components/icons';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import {
  createSisAsset,
  updateSisAsset,
  type SisAsset,
  type SisCustomField,
} from '@/api/sisAssets';

const ASSET_TYPES = ['Carpet Stand', 'Mat Stand', 'Gandola', 'Towel Stand', 'Rack', 'Iron Rack (Bedsheet)', 'Double Bed Set'];
const MATERIALS = ['Wood', 'Iron', 'Steel', 'MDF'];

interface Props {
  open: boolean;
  partyId: number;
  editing: SisAsset | null;
  onClose: () => void;
}

function blank(): SisAsset {
  return { id: 0, assetType: '', quantity: 1, material: '', size: '', remarks: '', customFields: [], updatedAt: '' };
}

export function SisAssetModal({ open, partyId, editing, onClose }: Props) {
  const qc = useQueryClient();
  const [f, setF] = useState<SisAsset>(blank());
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setF(editing ? { ...editing, customFields: [...editing.customFields] } : blank());
    setErr('');
  }, [open, editing]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        partyId,
        assetType: f.assetType.trim(),
        quantity: f.quantity,
        material: f.material.trim() || undefined,
        size: f.size.trim() || undefined,
        remarks: f.remarks.trim() || undefined,
        customFields: f.customFields.filter((c) => c.label.trim() || c.value.trim()),
      };
      return editing ? updateSisAsset(editing.id, payload) : createSisAsset(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sis-store', partyId] });
      qc.invalidateQueries({ queryKey: ['sis-asset-stores'] });
      onClose();
    },
  });

  function submit() {
    if (!f.assetType.trim()) { setErr('Asset type is required'); return; }
    if (!Number.isFinite(f.quantity) || f.quantity < 0) { setErr('Quantity must be 0 or more'); return; }
    save.mutate();
  }

  const setCustom = (i: number, patch: Partial<SisCustomField>) =>
    setF({ ...f, customFields: f.customFields.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit asset' : 'Add asset'}
      size="md"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save asset'}
          </Button>
        </>
      }
    >
      <datalist id="sis-asset-types">{ASSET_TYPES.map((t) => <option key={t} value={t} />)}</datalist>
      <datalist id="sis-asset-materials">{MATERIALS.map((m) => <option key={m} value={m} />)}</datalist>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormGroup label="Asset type" required error={err && !f.assetType.trim() ? err : null}>
          <Input list="sis-asset-types" value={f.assetType} onChange={(e) => setF({ ...f, assetType: e.target.value })} placeholder="e.g. Carpet Stand" />
        </FormGroup>
        <FormGroup label="Quantity" required>
          <Input type="number" min={0} value={f.quantity || ''} onChange={(e) => setF({ ...f, quantity: Number(e.target.value) })} className="text-right" />
        </FormGroup>
        <FormGroup label="Material">
          <Input list="sis-asset-materials" value={f.material} onChange={(e) => setF({ ...f, material: e.target.value })} placeholder="e.g. Iron" />
        </FormGroup>
        <FormGroup label="Size">
          <Input value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} placeholder="e.g. 3x3 / big" />
        </FormGroup>
        <FormGroup label="Remarks" className="sm:col-span-2">
          <Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} placeholder="e.g. Standing, near entrance" />
        </FormGroup>
      </div>

      {/* Custom fields — per-store extra attributes. */}
      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Custom fields</span>
          <Button size="sm" variant="glass" icon={<Plus size={12} />} onClick={() => setF({ ...f, customFields: [...f.customFields, { label: '', value: '' }] })}>
            Add field
          </Button>
        </div>
        {f.customFields.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)]">No custom fields. Use these for store-specific attributes (e.g. Colour, Condition, Vendor).</p>
        ) : (
          <div className="space-y-2">
            {f.customFields.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={c.label} onChange={(e) => setCustom(i, { label: e.target.value })} placeholder="Label (e.g. Colour)" className="flex-1" />
                <Input value={c.value} onChange={(e) => setCustom(i, { value: e.target.value })} placeholder="Value (e.g. Brown)" className="flex-1" />
                <button
                  onClick={() => setF({ ...f, customFields: f.customFields.filter((_, idx) => idx !== i) })}
                  className="flex-shrink-0 rounded p-1.5 text-[var(--text-muted)] hover:bg-danger/10 hover:text-danger"
                  aria-label="Remove field"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {save.isError && <p className="mt-3 text-xs text-danger">Could not save the asset. Please try again.</p>}
    </Modal>
  );
}
